import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconBarChart } from '../../assets/icons/IconBarChart';
import { IconInvoiceWarning } from '../../assets/icons/IconInvoiceWarning';
import { IconEmail } from '../../assets/icons/IconEmail';
import { IconFileGeneric } from '../../assets/icons/IconFileGeneric';
import { IconLink } from '../../assets/icons/IconLink';
import { IconHistory } from '../../assets/icons/IconHistory';
import { IconCheck } from '../../assets/icons/IconCheck';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { useTransphereStatus } from '../../hooks/useTransphereStatus';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { openTransphereMarketingSite } from '../../utils/portal/openInPortal';
import { TranslationKey } from '../../i18n/translationKeys';
import { OnboardingHeader } from './OnboardingHeader';
import { ValuePropRow } from './ValuePropRow';

export const ScreenValueProps: React.FC = () => {
    const { t } = useTranslation();
    const isAcomba = useIsAcomba();
    // Screens 1-3 run before any sync (see Onboarding.tsx), so tasksStore.dailyTasks isn't populated
    // yet — this reuses the same live check as the Home Transphere banner instead of duplicating it.
    // A `null` variant covers both "genuinely active" and "not yet resolved"; either way the item
    // renders enabled, matching the banner's own optimistic default while the check is in flight.
    const transphereVariant = useTransphereStatus();
    const encaissementActive = transphereVariant === null;
    const items = TranslationKey.Onboarding.ValueProps.Items;

    return (
        <div data-testid="onboarding-screen-value-props" className="flex flex-col items-center gap-xl w-full max-w-md">
            <OnboardingHeader
                title={t(TranslationKey.Onboarding.ValueProps.Title)}
                subtitle={t(TranslationKey.Onboarding.ValueProps.Subtitle)}
            />

            <div className="w-full">
                <ValuePropRow icon={<IconBarChart />} label={t(items.Summary)} />
                <ValuePropRow icon={<IconInvoiceWarning />} label={t(items.UnpaidInvoices)} />
                <ValuePropRow icon={<IconEmail />} label={t(items.PersonalizedEmails)} />
                <ValuePropRow icon={<IconFileGeneric />} label={t(items.Attachments)} />
                <ValuePropRow
                    icon={<IconLink />}
                    disabled={!encaissementActive}
                    label={t(items.PaymentLink)}
                    secondary={!encaissementActive && (
                        <>
                            {t(TranslationKey.Onboarding.ValueProps.PaymentLinkNotIncluded)}
                            {!isAcomba && (
                                <>
                                    {' — '}
                                    <LinkButton
                                        variant="navigation"
                                        size="s"
                                        data-testid="onboarding-payment-link-learn-more"
                                        onClick={openTransphereMarketingSite}
                                    >
                                        {t(TranslationKey.Home.TransphereBanner.LearnMore)}
                                    </LinkButton>
                                </>
                            )}
                        </>
                    )}
                />
                <ValuePropRow icon={<IconHistory />} label={t(items.History)} last />
            </div>

            <div className="inline-flex items-center gap-s rounded-full bg-accent-100 text-accent-400 text-s font-normal px-m py-s mb-m">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-accent-400 text-primary-50 shrink-0">
                    <IconCheck className="w-2.5 h-2.5" />
                </span>
                {t(TranslationKey.Onboarding.ValueProps.SuggestsBadge)}
            </div>
        </div>
    );
};
